import axios, { AxiosInstance } from 'axios';

// API base URL - update this based on your environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add JWT token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.api.post('/admin/login', { email, password });
    return response.data;
  }

  // Admin endpoints
  async getAssociations() {
    const response = await this.api.get('/admin/associations');
    return response.data;
  }

  async getAssociation(associationId: string) {
    const response = await this.api.get(`/admin/associations/${associationId}`);
    return response.data;
  }

  async createAssociation(data: { name: string; image?: string }) {
    const response = await this.api.post('/admin/associations', data);
    return response.data;
  }

  async getAssociationUsers(associationId: string) {
    const response = await this.api.get(`/admin/associations/${associationId}/users`);
    return response.data;
  }

  async onboardUser(associationId: string, data: {
    email: string;
    fullName: string;
    unitNumber: string;
    isBoardMember: boolean;
    type: 'homeowner' | 'tenant';
  }) {
    const response = await this.api.post(`/admin/associations/${associationId}/onboard`, {
      ...data,
      associationId, // Include associationId in the body as required by OnboardUserDto
    });
    return response.data;
  }

  async getDashboardStats() {
    const response = await this.api.get('/admin/stats');
    return response.data;
  }

  async uploadImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/admin/upload-association-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateUser(userId: string, data: {
    fullName?: string;
    email?: string;
    isGroupAdmin?: boolean;
    isBoardMember?: boolean;
    isVerified?: boolean;
    unitNumber?: string;
    type?: 'homeowner' | 'tenant';
  }) {
    const response = await this.api.patch(`/admin/users/${userId}`, data);
    return response.data;
  }

  async deleteUser(userId: string) {
    const response = await this.api.delete(`/admin/users/${userId}`);
    return response.data;
  }

  async freezeAssociation(associationId: string, isFrozen: boolean) {
    const response = await this.api.patch(`/admin/associations/${associationId}/freeze`, { isFrozen });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
